import { Box, Stack, Typography } from '@mui/material';
import { FC, useEffect, useRef } from 'react';
import { extractReunionCourses, filterCourses } from '../../../utils/format';
import { CourseFilter, FlattedCourse, Programme } from '../services/type';
import CourseCard from './course/CourseCard';
import { StatusCourse } from '../services/enum';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Props {
  program?: Programme;
  filter: CourseFilter;
}

const List: FC<Props> = ({ program, filter }) => {
  const navigate = useNavigate();
  const [searchParam, setSearchParams] = useSearchParams();
  const firstProgrammedRef = useRef<HTMLDivElement | null>(null);
  const topOfListRef = useRef<HTMLDivElement | null>(null);
  let firstCourseProgrammed: FlattedCourse | null = null;
  const courses = extractReunionCourses(program?.reunions);
  const filteredCourse = filterCourses(courses, filter);
  const hasFilter = filter.discipline || filter.montant || filter.minParticipant || filter.maxParticipant || filter.reunion

  const handleSelectCourse = (course: FlattedCourse | null) => {
    navigate(`pronos/${course?.reunionNum}/${course?.courseNum}`);
  };

  const handleClearFilters = () => {
    const params = new URLSearchParams(searchParam);
    params.delete('reunion');
    params.delete('discipline');
    params.delete('participants');

    setSearchParams(params, { replace: true });

  };

  // Scroll automatique
  useEffect(() => {
    if (hasFilter && topOfListRef.current) {
      topOfListRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else if (firstProgrammedRef.current) {
      firstProgrammedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [filter]);

  if (!program) return <Box
    display={'flex'}
    justifyContent={'center'}
    alignItems={'center'}
    height={'80vh'}
  >
    <Typography color="text.secondary" textAlign="center">
      Aucun pronostics disponnibles aujourd'hui
    </Typography>
  </Box>;

  return filteredCourse.length === 0 ? (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="80vh"
      gap={1.5}
    >
      <Typography color="text.secondary" textAlign="center">
        Aucune course ne correspond à vos filtres
      </Typography>

      <Typography
        onClick={handleClearFilters}
        variant="caption"
        sx={{
          cursor: 'pointer',
          color: 'primary.main',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': {
            textDecoration: 'underline',
            opacity: 0.85,
          },
        }}
      >
        Supprimer les filtres
      </Typography>
    </Box>

  ) : (
    <Stack
      display={'flex'}
      flexDirection={'column'}
      spacing={1.5}
      alignItems={'center'}
      px={1}
      sx={{ overflowY: 'auto', maxHeight: '85vh' }}
    >
      <Typography
        ref={topOfListRef}
        variant="subtitle1"
        color="text.secondary"
      >{`${filteredCourse.length} courses correspondent à vos filtres`}</Typography>
      {filteredCourse.map((c) => {
        if (
          c.course.categorieStatut === StatusCourse.A_PARTIR &&
          firstCourseProgrammed === null
        ) {
          firstCourseProgrammed = c;
        }
        return (
          <CourseCard
            key={`${c.reunionNum} - ${c.courseNum}`}
            course={c}
            reference={
              c === firstCourseProgrammed ? firstProgrammedRef : undefined
            }
            onClick={handleSelectCourse}
          />
        );
      })}
    </Stack>
  );
};

export default List;
